<script lang='ts'>
    import { createEventDispatcher, type ComponentProps, type ComponentType } from "svelte";
    import { type Writable, derived } from "svelte/store";
    import { format } from "date-fns";
    import type { ProgramAchievability, RaceAchievability, HoursAchievability } from "../modules/ProgramAchievability"
    import ProgramAchievabilityStatusIcon from "./ProgramAchievabilityStatusIcon.svelte";
    import Accordion from "../../Common/Accordion.svelte";
    import { MpaItem, DifficultyItem, CarbRateItem, EventReadinessItem, MaxWeeklyHoursItem } from "./RaceAchievabilityItems";
    import { XfaiBall } from "@/Common";
    import { type RacePlan } from "@/Common/constants";
  import EventReadiness from "./EventReadiness.svelte";

    export let programAchievability: ProgramAchievability
    export let analyzeRaceRunning = false
    export let targetDate: Date
    export let plan: RacePlan

    const dispatch = createEventDispatcher()

    let {raceAchievability, hoursAchievability, overallAchievability} = programAchievability

    interface AchievabilityItems {
        "Power": {
            component: ComponentType<MpaItem>,
            data: ComponentProps<MpaItem>,
        },
        "Carb Rate" : {
            component: ComponentType<CarbRateItem>,
            data: ComponentProps<CarbRateItem>,
        },
        "Event Readiness" : {
            component: ComponentType<EventReadinessItem>,
            data: ComponentProps<EventReadinessItem>,
        },
        "Difficulty" : {
            component: ComponentType<DifficultyItem>,
            data: ComponentProps<DifficultyItem>,
        },
        "Max Weekly Hours" : {
            component: ComponentType<MaxWeeklyHoursItem>,
            data: ComponentProps<MaxWeeklyHoursItem>,
        },
    }

    let achievabilityItems = derived<[Writable<RaceAchievability>, Writable<HoursAchievability>], AchievabilityItems>(
        [raceAchievability, hoursAchievability],
        ([$raceAchievability, $hoursAchievability]) => {
            return {
                "Power": {
                    data: $raceAchievability.mpa,
                    component: MpaItem,
                },
                "Carb Rate": {
                    data: {...$raceAchievability.carb_rate, carb_rate_target: plan.carbsPerHour, ignore_carb_rate: plan.ignoreCarbs},
                    component: CarbRateItem,
                },
                "Event Readiness": {
                    data: {...$raceAchievability.event_readiness, event_readiness_target: plan.settings.taper},
                    component: EventReadinessItem,
                },
                "Difficulty": {   
                    data: {...$raceAchievability.difficulty, difficulty_target: plan.difficultyScore, ignore_difficulty: plan.ignoreDifficulty},
                    component: DifficultyItem,
                },
                "Max Weekly Hours": {
                    data: {...$hoursAchievability, hours_target: plan.settings.maxWeeklyHours},
                    component: MaxWeeklyHoursItem,
                },
            }
        }
    )
</script>

<div class="flex flex-col gap-7 text-base">
    <div class="grid grid-cols-[min-content,auto] sm:grid-cols-[min-content,1fr,min-content,1fr] xl:grid-cols-[min-content,auto] items-baseline gap-x-3 gap-y-2">

        {#each Object.entries($achievabilityItems) as [title, item]}
            {#if analyzeRaceRunning}
                <i class="fas fa-circle-notch fa-spin" />
            {:else}
                {#if (item.component === DifficultyItem && item.data.ignore_difficulty) || (item.component === CarbRateItem && item.data.ignore_carb_rate)}
                <span class="text-slate-500 text-center">_</span>
                {:else}
                <ProgramAchievabilityStatusIcon showCheck status={item.data.status} />
                {/if}
                
            {/if}
            <Accordion class="border-b border-black/10 border-solid border-x-0 border-t-0 sm:mr-2 xl:mr-0">
                <div slot="trigger">
                    {title}
                </div>
                <div slot="content">
                    <svelte:component this={item.component} {...item.data} />
                </div>
            </Accordion>
        {/each}

    </div>

    <div class="py-3 px-5 border border-black/10 border-solid rounded shadow-sm shadow-black/10">
        <div class="grid items-center grid-cols-[min-content,auto] xl:grid-flow-row xl:grid-cols-1 xl:justify-items-center xl:text-center gap-4">
            <XfaiBall class="text-xerteal-800 w-16" type={analyzeRaceRunning ? "animated" : "static"} />

            {#if analyzeRaceRunning}
                <span>Determining achievability...</span>
            {:else if $hoursAchievability.status === "error"}
                <span>The weekly hours required to complete this race are not feasible..</span>
            {:else if $raceAchievability.readyBy === "now" && $overallAchievability === "warning"}
                <span>Your targets should be achievable but flexibility is limited. You can run the forecast but the plan may be difficult to follow.</span>
            {:else if $raceAchievability.readyBy === "now"}
                <span>Your targets are likely achievable, run the forecast!</span>
            {:else if $raceAchievability.readyBy instanceof Date}
                <span class="col-start-2 xl:col-start-1">
                    Your targets aren't achievable by
                    <span class="font-bold">{format(targetDate, 'LLLL dd, yyyy')}</span>
                    but should be achievable by
                    <span class="font-bold">{format($raceAchievability.readyBy, 'LLLL dd, yyyy')}</span>
                </span>
                <button class="col-span-2 row-start-2 justify-self-end md:justify-self-auto md:col-span-1 md:row-start-1 md:col-start-3 xl:col-start-1 xl:row-start-3 xl:justify-self-stretch btn-xfai border border-slate-600 text-slate-600 bg-white hover:bg-[--color-xert-blue] hover:text-white hover:border-[--color-xert-blue]" on:click={(e)=>dispatch("changeDate", e.detail)}>
                    Change Date
                </button>
                
            {:else}
                <span>Your targets aren't achievable within the year.</span>
            {/if}
        </div>
    </div>
</div>
